/**
 * Safe localStorage wrapper that gracefully handles SecurityError exceptions.
 *
 * Some browsers (e.g., Firefox in strict private browsing, or browsers with
 * third-party storage blocked) throw a SecurityError when any localStorage
 * property is accessed. These helpers catch those errors and return sensible
 * defaults so the rest of the application can react accordingly.
 */

const STORAGE_UNAVAILABLE_MESSAGE =
  "Your browser has blocked access to local storage. " +
  "Please enable cookies and local storage in your browser settings to use this application.";

/**
 * Returns true if localStorage is available and accessible.
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = "__atlas_ls_test__";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely retrieves a value from localStorage.
 * Returns null when localStorage is unavailable or the key does not exist.
 */
export const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * Safely stores a value in localStorage.
 * Throws a user-friendly Error when localStorage is unavailable so callers
 * (e.g., the login action) can surface a helpful message to the user.
 */
export const safeSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    throw new Error(STORAGE_UNAVAILABLE_MESSAGE);
  }
};

/**
 * Safely removes a key from localStorage.
 * Silently ignores errors – if storage is blocked the key was never written.
 */
export const safeRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Silently ignore – nothing to remove if storage was never accessible.
  }
};
